import axios from "axios";
import authHeader from "src/services/auth-header";
import { refreshAccess, logout } from "src/redux/slices/Auth";
import { showAlert } from "src/redux/slices/AlertSlice";
import i18n from "src/i18n";

const getPreferredLanguage = () =>
    process.env.REACT_APP_LANG ||
    i18n.resolvedLanguage ||
    i18n.language ||
    localStorage.getItem("i18nextLng") ||
    navigator.language ||
    "de";

const getJsonHeaders = () => {
    const language = getPreferredLanguage();
    return {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Accept-Language": language,
        ...authHeader(),
    };
};

const dispatchError = (dispatch, error) => {
    const message =
        (error.response &&
            error.response.data &&
            error.response.data.message) ||
        error.message ||
        error.toString();
    dispatch(
        showAlert({
            message: message,
            severity: "error",
        })
    );
};

/**
 * Send an authenticated POST request and retry it once after token refresh.
 *
 * @param {Function} dispatch Redux dispatch used by authentication handling.
 * @param {string} endpoint Relative API endpoint.
 * @param {*} body Request payload.
 * @param {object} requestConfig Optional Axios settings such as responseType.
 * @returns {Promise<object>} Axios response.
 */
export const post = async (dispatch, endpoint, body, requestConfig = {}) => {
    const _post = (endpoint, body) => {
        const config = {
            ...requestConfig,
            headers: {
                ...getJsonHeaders(),
                ...(requestConfig.headers || {}),
            },
        };
        return axios.post(`${endpoint}`, body, config).then((response) => {
            return response;
        });
    };

    try {
        const res = await _post(endpoint, body);
        return res;
    } catch (error) {
        if (!error.response) {
            dispatchError(dispatch, "Server unreachable");
            dispatch(logout);
        } else if (error.response.status === 401) {
            // unathorized
            try {
                await dispatch(refreshAccess());
                // re-try
                try {
                    const res = await _post(endpoint, body);
                    return res;
                } catch (error) {
                    dispatchError(dispatch, error);
                    dispatch(logout);
                    throw error;
                }
            } catch (rejectedValueOrSerializedError) {
                throw Error("Your session has expired");
            }
        } else {
            throw error;
        }
    }
};

export const put = async (dispatch, endpoint, body) => {
    const _put = (endpoint, body) => {
        const config = {
            headers: getJsonHeaders(),
        };
        return axios.put(`${endpoint}`, body, config).then((response) => {
            return response;
        });
    };

    try {
        const res = await _put(endpoint, body);
        return res;
    } catch (error) {
        if (!error.response) {
            dispatchError(dispatch, "Server unreachable");
            dispatch(logout);
        } else if (error.response.status === 401) {
            // unathorized
            try {
                await dispatch(refreshAccess());
                // re-try
                try {
                    const res = await _put(endpoint, body);
                    return res;
                } catch (error) {
                    dispatchError(dispatch, error);
                    dispatch(logout);
                    throw error;
                }
            } catch (rejectedValueOrSerializedError) {
                throw Error("Your session has expired");
            }
        } else {
            throw error;
        }
    }
};

export const get = async (dispatch, endpoint) => {
    const _get = (endpoint) => {
        const config = {
            headers: getJsonHeaders(),
        };
        return axios.get(`${endpoint}`, config).then((response) => {
            return response;
        });
    };

    try {
        const res = await _get(endpoint);
        return res;
    } catch (error) {
        if (!error.response) {
            dispatchError(dispatch, "Server unreachable");
            dispatch(logout);
        } else if (error.response.status === 401) {
            // unathorized
            try {
                await dispatch(refreshAccess());
                // re-try
                try {
                    const res = await _get(endpoint);
                    return res;
                } catch (error) {
                    dispatchError(dispatch, error);
                    dispatch(logout);
                    throw error;
                }
            } catch (rejectedValueOrSerializedError) {
                throw Error("Your session has expired");
            }
        } else {
            throw error;
        }
    }
};

export const del = async (dispatch, endpoint) => {
    const _del = (endpoint) => {
        const config = {
            headers: getJsonHeaders(),
        };
        return axios.delete(`${endpoint}`, config).then((response) => {
            return response;
        });
    };

    try {
        const res = await _del(endpoint);
        return res;
    } catch (error) {
        if (!error.response) {
            dispatchError(dispatch, "Server unreachable");
            dispatch(logout);
        } else if (error.response.status === 401) {
            // unauthorized → refresh token
            try {
                await dispatch(refreshAccess());
                // retry
                try {
                    const res = await _del(endpoint);
                    return res;
                } catch (error) {
                    dispatchError(dispatch, error);
                    dispatch(logout);
                    throw error;
                }
            } catch {
                throw Error("Your session has expired");
            }
        } else {
            throw error;
        }
    }
};
